/**
 * CertificateView — Renders a printable Certificate of Completion using
 * the FDAcademy brand design. Loads the certificate either from the user's own
 * `/api/lms/courses/{course_id}/certificate` endpoint (if this route is
 * `/certificate/course/:course_id`) or the public
 * `/api/verify/{cert_number}` endpoint (for `/certificate/:cert_number`).
 *
 * Layout intentionally mirrors the base template supplied by the user:
 *   - Angled navy corners
 *   - FDAcademy logo + wordmark
 *   - CERTIFICATE OF COMPLETION headline
 *   - Recipient name + course
 *   - Achieved-on date
 *   - Twin signature lines (Academy Director / Instructor)
 *   - Excellence-in-lyophilization seal
 *   - Certificate number + QR verify block
 */
import React, { useContext, useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import { AuthContext } from '../App';
import { Button } from '../components/ui/button';
import {
  ArrowLeft, Printer, ShieldCheck, XCircle, Loader2, Share2, Linkedin, ImageIcon, FileText,
} from 'lucide-react';
import { toast } from 'sonner';
import useDocumentMeta from '../hooks/useDocumentMeta';
import KudosButton from '../components/KudosButton';

const fmtDate = (iso) => {
  if (!iso) return '';
  try { return new Date(iso).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' }); }
  catch (_) { return iso; }
};

/** Placeholder QR — we render as a scannable data URI via a lightweight
 * on-the-fly generator. For printer-safety we fall back to a text URL if
 * QR generation isn't available. */
const QRBox = ({ url, centered = false }) => (
  <div className={`inline-flex items-center gap-3 border border-navy/20 rounded-xl p-2.5 bg-white ${centered ? '' : ''}`}>
    <img
      src={`https://api.qrserver.com/v1/create-qr-code/?size=90x90&data=${encodeURIComponent(url)}`}
      alt="Verification QR code"
      className="h-[56px] w-[56px]"
      onError={(e) => { e.currentTarget.style.display = 'none'; }}
    />
    <div className={`text-left max-w-[240px] ${centered ? 'text-center' : ''}`}>
      <div className="text-[9px] uppercase tracking-[0.14em] font-extrabold text-navy">Verify this certificate</div>
      <div className="text-[9px] text-navy/70">Scan or visit</div>
      <div className="text-[9px] font-mono text-blue-600 mt-0.5 truncate">{url.replace(/^https?:\/\//, '')}</div>
    </div>
  </div>
);

/**
 * Reusable gold-foil excellence seal — SELF-CONTAINED (its own gradient defs
 * with unique IDs) so it renders identically regardless of parent SVG scope.
 * This is the exact same visual used on the Certificate of Completion.
 */
let _sealUid = 0;
const GoldExcellenceSeal = ({ size = 'lg' }) => {
  const dim = size === 'sm' ? 'h-24 w-24' : size === 'md' ? 'h-32 w-32' : 'h-48 w-48';
  const uid = useMemo(() => `seal-${++_sealUid}`, []);
  const foilId = `${uid}-foil`;
  const sealId = `${uid}-seal`;
  const sheenId = `${uid}-sheen`;
  return (
    <svg viewBox="0 0 200 200" className={`${dim} flex-shrink-0`} aria-hidden="true" data-testid="cert-seal-gold">
      <defs>
        <linearGradient id={foilId} x1="0" y1="0" x2="1" y2="1">
          <stop offset="0" stopColor="#f6dfa1" />
          <stop offset="0.35" stopColor="#f4c545" />
          <stop offset="0.55" stopColor="#e6a91a" />
          <stop offset="0.75" stopColor="#f4c545" />
          <stop offset="1" stopColor="#a67c11" />
        </linearGradient>
        <radialGradient id={sealId} cx="0.4" cy="0.35" r="0.75">
          <stop offset="0" stopColor="#fff2c8" />
          <stop offset="0.35" stopColor="#f4c545" />
          <stop offset="0.7" stopColor="#c99019" />
          <stop offset="1" stopColor="#805e0a" />
        </radialGradient>
        <linearGradient id={sheenId} x1="0" y1="0" x2="1" y2="1">
          <stop offset="0" stopColor="#fff2c8" stopOpacity="0.95" />
          <stop offset="0.5" stopColor="#f4c545" stopOpacity="0.2" />
          <stop offset="1" stopColor="#805e0a" stopOpacity="0.5" />
        </linearGradient>
      </defs>
      <g transform="translate(100 100)">
        {/* Outer starburst rays */}
        {Array.from({ length: 28 }).map((_, i) => (
          <rect
            key={i}
            x="-7"
            y="-96"
            width="14"
            height="22"
            fill={`url(#${foilId})`}
            transform={`rotate(${i * (360 / 28)})`}
          />
        ))}
        {/* Seal body */}
        <circle r="76" fill={`url(#${sealId})`} stroke="#805e0a" strokeWidth="2" />
        <circle r="66" fill="none" stroke="#805e0a" strokeWidth="0.8" />
        {/* Sheen overlay */}
        <ellipse cx="-22" cy="-28" rx="46" ry="26" fill={`url(#${sheenId})`} opacity="0.6" />
        {/* Text */}
        <text textAnchor="middle" y="-30" style={{ fontSize: '11px', letterSpacing: '2.4px' }} fill="#5a3d05" fontWeight="900">COMMITMENT TO</text>
        <text textAnchor="middle" y="-8" style={{ fontSize: '18px', letterSpacing: '2px' }} fill="#5a3d05" fontWeight="900">EXCELLENCE</text>
        <text textAnchor="middle" y="10" style={{ fontSize: '10px', letterSpacing: '2.4px' }} fill="#5a3d05" fontWeight="900">IN</text>
        <text textAnchor="middle" y="30" style={{ fontSize: '14px', letterSpacing: '1.8px' }} fill="#5a3d05" fontWeight="900">LYOPHILIZATION</text>
        {/* Star flourish */}
        <polygon points="0,44 3.5,52 13,52 5.5,58 8,68 0,63 -8,68 -5.5,58 -13,52 -3.5,52" fill="#5a3d05" />
      </g>
    </svg>
  );
};

/**
 * Kind-specific sheets. Each is 1120×880 so they share the wrapper's scale.
 */

const DesignationSheet = ({ cert, verifyUrl }) => (
  <div
    className="mx-auto bg-white text-navy relative overflow-hidden"
    style={{ width: '1120px', height: '880px', boxShadow: '0 30px 80px -20px rgba(11, 30, 63, 0.35)' }}
    data-testid="certificate-sheet"
  >
    <div className="absolute inset-0 pointer-events-none">
      <svg viewBox="0 0 1120 880" className="w-full h-full" preserveAspectRatio="none">
        <defs>
          <pattern id="hex" x="0" y="0" width="60" height="52" patternUnits="userSpaceOnUse">
            <polygon points="30,2 58,17 58,45 30,60 2,45 2,17" fill="none" stroke="#1e63c9" strokeOpacity="0.13" strokeWidth="0.7" />
          </pattern>
        </defs>
        <rect x="0" y="0" width="1120" height="880" fill="url(#hex)" />
        {/* Corners */}
        <polygon points="0,0 260,0 0,220" fill="#0b1e3f" />
        <polygon points="1120,0 900,0 1120,220" fill="#0b1e3f" />
        <polygon points="0,880 260,880 0,680" fill="#0b1e3f" />
        <polygon points="1120,880 900,880 1120,680" fill="#0b1e3f" />
        <rect x="34" y="34" width="1052" height="812" fill="none" stroke="#c0c0c0" strokeWidth="4" />
        <rect x="42" y="42" width="1036" height="796" fill="none" stroke="#1e63c9" strokeOpacity="0.55" strokeWidth="1.5" />
        {/* Snowflake side accents */}
        <g fill="#ffffff" opacity="0.9" transform="translate(70 440)"><circle r="14" fill="#0b1e3f" /><text textAnchor="middle" y="6" fontSize="18">❄</text></g>
        <g fill="#ffffff" opacity="0.9" transform="translate(1050 440)"><circle r="14" fill="#0b1e3f" /><text textAnchor="middle" y="6" fontSize="18">❄</text></g>
      </svg>
    </div>
    <div className="relative h-full flex flex-col items-center px-24 pt-14 pb-6">
      <img src="/brand-logo-transparent.png" alt="FDAcademy" className="h-[150px] w-auto" style={{ mixBlendMode: 'multiply' }} data-testid="cert-brand-logo" />
      <h1 className="font-heading font-black mt-3 tracking-[0.02em]" style={{ fontSize: '58px', color: '#0b1e3f', lineHeight: 1 }}>
        PROFESSIONAL DESIGNATION
      </h1>
      <div className="font-heading font-bold tracking-[0.32em] text-[13px] mt-1.5 flex items-center gap-4" style={{ color: '#8a8a8a' }}>
        <span className="inline-block w-16 h-px bg-current" />
        DESIGNATION OF ACHIEVEMENT
        <span className="inline-block w-16 h-px bg-current" />
      </div>

      <div className="mt-6 text-[11px] tracking-[0.4em] font-bold text-navy/70">THIS DESIGNATION IS AWARDED TO</div>
      <div className="mt-2 rounded-xl border-2 border-blue-500/50 w-full max-w-[820px] py-3 text-center">
        <div className="font-heading text-[34px] font-extrabold text-navy" data-testid="cert-holder-name">
          {cert.holder_name || '\u00A0'}
        </div>
        <div className="text-[10px] tracking-[0.4em] font-bold text-blue-500/80 mt-1">RECIPIENT NAME</div>
      </div>

      <div className="mt-4 text-[11px] tracking-[0.4em] font-bold text-navy/70">FOR SUCCESSFULLY COMPLETING THE REQUIREMENTS OF THE</div>
      <div className="mt-2 rounded-xl border-2 border-blue-500/50 w-full max-w-[820px] py-3 text-center">
        <div className="font-heading text-[24px] font-extrabold text-blue-700 italic" data-testid="cert-course-title">
          {cert.designation_title || cert.course_title || '\u00A0'}
        </div>
        <div className="text-[10px] tracking-[0.4em] font-bold text-blue-500/80 mt-1">DESIGNATION TITLE</div>
      </div>

      <p className="mt-4 max-w-[820px] text-center text-[13px] text-navy/70 italic">
        This designation recognizes advanced learning achievement, applied knowledge, and demonstrated commitment to
        professional development in freeze drying.
      </p>

      <div className="mt-auto w-full grid grid-cols-3 items-end gap-4 pt-4">
        <div className="text-center pb-1">
          <div className="italic font-heading text-[16px] text-navy pb-0.5">{cert.academy_director || 'Freeze Dry Academy'}</div>
          <div className="border-t-2 border-navy w-48 mx-auto" />
          <div className="text-[9px] tracking-[0.32em] font-bold text-navy/70 mt-1">ACADEMY DIRECTOR</div>
        </div>
        {/* Gold foil Excellence seal — same as Certificate of Completion */}
        <div className="flex justify-center">
          <GoldExcellenceSeal size="md" />
        </div>
        <div className="text-center pb-1">
          <div className="italic font-heading text-[16px] text-navy pb-0.5">{cert.instructor_name || 'Freeze Dry Academy'}</div>
          <div className="border-t-2 border-navy w-48 mx-auto" />
          <div className="text-[9px] tracking-[0.32em] font-bold text-navy/70 mt-1">AUTHORIZED BY FREEZE DRY ACADEMY</div>
        </div>
      </div>

      <div className="mt-3 grid grid-cols-3 gap-6 w-full max-w-[820px]" data-testid="cert-number-block">
        <div className="text-center">
          <div className="text-[9px] tracking-[0.32em] font-extrabold text-navy/60">DATE AWARDED</div>
          <div className="font-mono text-[13px] font-extrabold text-navy mt-1">{fmtDate(cert.issued_at)}</div>
        </div>
        <div className="text-center">
          <div className="text-[9px] tracking-[0.32em] font-extrabold text-navy/60">DESIGNATION NUMBER</div>
          <div className="font-mono text-[13px] font-extrabold text-navy mt-1" data-testid="cert-number">{cert.cert_number}</div>
        </div>
        <div className="text-center flex justify-center" data-testid="cert-qr-block">
          <QRBox url={verifyUrl} centered />
        </div>
      </div>
    </div>
  </div>
);

const SpecialtySheet = ({ cert, verifyUrl }) => (
  <div
    className="mx-auto bg-white text-navy relative overflow-hidden"
    style={{ width: '1120px', height: '880px', boxShadow: '0 30px 80px -20px rgba(11, 30, 63, 0.35)' }}
    data-testid="certificate-sheet"
  >
    <div className="absolute inset-0 pointer-events-none">
      <svg viewBox="0 0 1120 880" className="w-full h-full" preserveAspectRatio="none">
        <defs>
          <linearGradient id="swirlBlue" x1="0" y1="0" x2="1" y2="1">
            <stop offset="0" stopColor="#dbeafe" stopOpacity="0.9" />
            <stop offset="1" stopColor="#93c5fd" stopOpacity="0.1" />
          </linearGradient>
        </defs>
        <rect x="0" y="0" width="1120" height="880" fill="#fbfdff" />
        {/* Full navy frame */}
        <rect x="0" y="0" width="1120" height="880" fill="none" stroke="#0b1e3f" strokeWidth="18" />
        <rect x="26" y="26" width="1068" height="828" fill="none" stroke="#c0c0c0" strokeWidth="3" />
        <rect x="38" y="38" width="1044" height="804" fill="none" stroke="#1e63c9" strokeOpacity="0.6" strokeWidth="1.5" />
        {/* Wispy swirls */}
        <path d="M 60,200 Q 200,120 260,300 Q 320,480 100,540 Q -40,600 60,700" fill="none" stroke="url(#swirlBlue)" strokeWidth="24" opacity="0.55" />
        <path d="M 1060,200 Q 920,120 860,300 Q 800,480 1020,540 Q 1160,600 1060,700" fill="none" stroke="url(#swirlBlue)" strokeWidth="24" opacity="0.55" />
        {/* Firework corner sparkles */}
        {[[110, 110], [1010, 110]].map(([cx, cy], i) => (
          <g key={i} stroke="#1e63c9" strokeOpacity="0.6" strokeWidth="1.5">
            {Array.from({ length: 12 }).map((_, k) => {
              const a = (k / 12) * Math.PI * 2;
              return <line key={k} x1={cx} y1={cy} x2={cx + Math.cos(a) * 46} y2={cy + Math.sin(a) * 46} />;
            })}
          </g>
        ))}
      </svg>
    </div>
    <div className="relative h-full flex flex-col items-center px-24 pt-12 pb-6">
      <img src="/brand-logo-transparent.png" alt="FDAcademy" className="h-[130px] w-auto" style={{ mixBlendMode: 'multiply' }} data-testid="cert-brand-logo" />
      <h1 className="font-heading font-black mt-3 tracking-[0.02em]" style={{ fontSize: '68px', color: '#0b1e3f', lineHeight: 1 }}>
        SPECIALTY CERTIFICATE
      </h1>
      <div className="font-heading font-bold tracking-[0.32em] text-[13px] mt-1.5 flex items-center gap-4" style={{ color: '#8a8a8a' }}>
        <span className="inline-block w-16 h-px bg-current" />
        AWARD OF SPECIALIZED ACHIEVEMENT
        <span className="inline-block w-16 h-px bg-current" />
      </div>

      <div className="mt-5 text-[11px] tracking-[0.4em] font-bold text-navy/70">✦ THIS CERTIFICATE IS PRESENTED TO ✦</div>
      <div className="mt-2 rounded-xl border-2 border-blue-500/50 w-full max-w-[820px] py-3 text-center">
        <div className="font-heading text-[32px] font-extrabold text-navy" data-testid="cert-holder-name">
          {cert.holder_name || '\u00A0'}
        </div>
        <div className="text-[10px] tracking-[0.4em] font-bold text-blue-500/80 mt-1">RECIPIENT NAME</div>
      </div>

      <div className="mt-3 text-[11px] tracking-[0.4em] font-bold text-navy/70">✦ IN RECOGNITION OF SPECIALIZED ACHIEVEMENT IN ✦</div>
      <div className="mt-2 rounded-xl border-2 border-blue-500/50 w-full max-w-[820px] py-2 text-center">
        <div className="font-heading text-[22px] font-extrabold text-blue-700 italic" data-testid="cert-course-title">
          {cert.specialty_title || cert.course_title || '\u00A0'}
        </div>
        <div className="text-[10px] tracking-[0.4em] font-bold text-blue-500/80 mt-1">SPECIALTY TITLE</div>
      </div>

      <div className="mt-2 text-[11px] tracking-[0.4em] font-bold text-navy/70">THROUGH</div>
      <div className="mt-1 rounded-xl border-2 border-blue-500/50 w-full max-w-[820px] py-2 text-center">
        <div className="font-heading text-[18px] font-extrabold text-navy">
          {cert.event_program || '\u00A0'}
        </div>
        <div className="text-[10px] tracking-[0.4em] font-bold text-blue-500/80 mt-1">EVENT / PROGRAM</div>
      </div>

      <p className="mt-3 max-w-[820px] text-center text-[12px] text-navy/70 italic">
        This certificate recognizes specialized participation, achievement, or completion in advanced Freeze Dry Academy events, workshops, or focused learning experiences.
      </p>

      <div className="mt-auto w-full grid grid-cols-3 items-end gap-4 pt-3">
        <div className="text-center pb-1">
          <div className="italic font-heading text-[16px] text-navy pb-0.5">{cert.academy_director || 'Freeze Dry Academy'}</div>
          <div className="border-t-2 border-navy w-48 mx-auto" />
          <div className="text-[9px] tracking-[0.32em] font-bold text-navy/70 mt-1">ACADEMY DIRECTOR</div>
        </div>
        {/* Gold foil Excellence seal — same visual as CoC, smallest size for dense Specialty layout */}
        <div className="flex justify-center">
          <GoldExcellenceSeal size="sm" />
        </div>
        <div className="text-center pb-1">
          <div className="italic font-heading text-[16px] text-navy pb-0.5">{cert.instructor_name || 'Freeze Dry Academy'}</div>
          <div className="border-t-2 border-navy w-48 mx-auto" />
          <div className="text-[9px] tracking-[0.32em] font-bold text-navy/70 mt-1">AUTHORIZED BY FREEZE DRY ACADEMY</div>
        </div>
      </div>

      <div className="mt-3 grid grid-cols-3 gap-6 w-full max-w-[820px]" data-testid="cert-number-block">
        <div className="text-center">
          <div className="text-[9px] tracking-[0.32em] font-extrabold text-navy/60">DATE AWARDED</div>
          <div className="font-mono text-[13px] font-extrabold text-navy mt-1">{fmtDate(cert.issued_at)}</div>
        </div>
        <div className="text-center">
          <div className="text-[9px] tracking-[0.32em] font-extrabold text-navy/60">CERTIFICATE NUMBER</div>
          <div className="font-mono text-[13px] font-extrabold text-navy mt-1" data-testid="cert-number">{cert.cert_number}</div>
        </div>
        <div className="text-center flex justify-center" data-testid="cert-qr-block">
          <QRBox url={verifyUrl} centered />
        </div>
      </div>
    </div>
  </div>
);

const CertificateSheet = ({ cert, verifyUrl, isPublic }) => {
  if (cert.kind === 'designation') return <DesignationSheet cert={cert} verifyUrl={verifyUrl} />;
  if (cert.kind === 'specialty') return <SpecialtySheet cert={cert} verifyUrl={verifyUrl} />;
  return <CourseSheet cert={cert} verifyUrl={verifyUrl} isPublic={isPublic} />;
};

const CourseSheet = ({ cert, verifyUrl, isPublic }) => (
  <div
    className="mx-auto bg-white text-navy relative overflow-hidden"
    style={{ width: '1120px', height: '880px', boxShadow: '0 30px 80px -20px rgba(11, 30, 63, 0.35)' }}
    data-testid="certificate-sheet"
  >
    {/* Angled navy corners */}
    <div className="absolute inset-0 pointer-events-none">
      <svg viewBox="0 0 1120 880" className="w-full h-full" preserveAspectRatio="none">
        <defs>
          <linearGradient id="cornerBlue" x1="0" y1="0" x2="1" y2="1">
            <stop offset="0" stopColor="#0b1e3f" />
            <stop offset="1" stopColor="#1e63c9" />
          </linearGradient>
          <linearGradient id="goldFoil" x1="0" y1="0" x2="1" y2="1">
            <stop offset="0" stopColor="#f6dfa1" />
            <stop offset="0.35" stopColor="#f4c545" />
            <stop offset="0.55" stopColor="#e6a91a" />
            <stop offset="0.75" stopColor="#f4c545" />
            <stop offset="1" stopColor="#a67c11" />
          </linearGradient>
          <radialGradient id="goldSeal" cx="0.4" cy="0.35" r="0.75">
            <stop offset="0" stopColor="#fff2c8" />
            <stop offset="0.35" stopColor="#f4c545" />
            <stop offset="0.7" stopColor="#c99019" />
            <stop offset="1" stopColor="#805e0a" />
          </radialGradient>
        </defs>
        <polygon points="0,0 260,0 0,220" fill="url(#cornerBlue)" opacity="0.95" />
        <polygon points="0,0 200,0 0,170" fill="#0b1e3f" />
        <polygon points="1120,0 900,0 1120,220" fill="url(#cornerBlue)" opacity="0.95" />
        <polygon points="1120,0 960,0 1120,180" fill="#0b1e3f" />
        <polygon points="0,880 260,880 0,680" fill="url(#cornerBlue)" opacity="0.95" />
        <polygon points="0,880 190,880 0,730" fill="#0b1e3f" />
        <polygon points="1120,880 900,880 1120,680" fill="url(#cornerBlue)" opacity="0.95" />
        <polygon points="1120,880 950,880 1120,725" fill="#0b1e3f" />
        <rect x="34" y="34" width="1052" height="812" fill="none" stroke="#1e63c9" strokeOpacity="0.45" strokeWidth="2" />
        <rect x="42" y="42" width="1036" height="796" fill="none" stroke="#0b1e3f" strokeOpacity="0.5" strokeWidth="1" />
      </svg>
    </div>

    {/* Content — pushed down so the logo can breathe below the corner triangles */}
    <div className="relative h-full flex flex-col items-center px-20 pt-16 pb-6">
      {/* Larger transparent logo — mix-blend-multiply hides any residual white */}
      <img
        src="/brand-logo-transparent.png"
        alt="Freeze Dry Academy"
        className="h-[160px] w-auto object-contain"
        style={{ mixBlendMode: 'multiply' }}
        draggable={false}
        data-testid="cert-brand-logo"
      />

      <h1
        className="font-heading font-black tracking-[0.03em] mt-2"
        style={{ fontSize: '54px', color: '#0b1e3f', letterSpacing: '0.04em', lineHeight: 1 }}
      >
        CERTIFICATE
      </h1>
      <div
        className="font-heading font-bold tracking-[0.32em] text-[14px] mt-1 flex items-center gap-4"
        style={{ color: '#1e63c9' }}
      >
        <span className="inline-block w-14 h-px bg-current" />
        OF COMPLETION
        <span className="inline-block w-14 h-px bg-current" />
      </div>

      <div className="mt-4 text-[10px] tracking-[0.4em] font-bold text-navy/70">THIS CERTIFIES THAT</div>
      <div
        className="mt-2 text-[30px] font-heading font-extrabold text-center border-b-2 border-navy/30 pb-1 px-16 min-w-[560px]"
        style={{ color: '#0b1e3f' }}
        data-testid="cert-holder-name"
      >
        {cert.holder_name || '\u00A0'}
      </div>
      <div className="mt-2 text-[10px] tracking-[0.4em] font-bold text-navy/70">HAS SUCCESSFULLY COMPLETED THE COURSE</div>
      <div
        className="mt-1 text-[20px] font-heading font-bold text-center border-b-2 border-blue-500/40 pb-1 px-16 min-w-[560px] italic"
        style={{ color: '#1e63c9' }}
        data-testid="cert-course-title"
      >
        {cert.course_title || '\u00A0'}
      </div>

      <div className="mt-2 flex items-center gap-3 text-[10px] tracking-[0.4em] font-bold text-navy/70">
        <span>ACHIEVED ON</span>
        <span className="border-b border-navy/40 pb-0.5 min-w-[200px] text-[13px] font-heading font-extrabold text-navy tracking-normal" data-testid="cert-issued-at">
          {fmtDate(cert.issued_at)}
        </span>
      </div>

      {/* Signature row — signatures LEFT + RIGHT, LARGE GOLD FOIL SEAL centre */}
      <div className="mt-3 w-full flex items-end justify-between gap-6 px-4">
        <div className="text-center pb-2">
          <div className="italic font-heading text-[18px] text-navy pb-0.5">
            {cert.academy_director || 'Freeze Dry Academy'}
          </div>
          <div className="border-t-2 border-navy w-52" />
          <div className="text-[9px] tracking-[0.32em] font-bold text-navy/70 mt-1">ACADEMY DIRECTOR</div>
        </div>

        {/* LARGE GOLD FOIL SEAL — shared component (identical across all cert kinds) */}
        <GoldExcellenceSeal size="lg" />

        <div className="text-center pb-2">
          <div className="italic font-heading text-[18px] text-navy pb-0.5">
            {cert.instructor_name || 'FDAcademy Faculty'}
          </div>
          <div className="border-t-2 border-navy w-52" />
          <div className="text-[9px] tracking-[0.32em] font-bold text-navy/70 mt-1">INSTRUCTOR</div>
        </div>
      </div>

      {/* Centred Certificate Number */}
      <div className="mt-3 text-center" data-testid="cert-number-block">
        <div className="text-[9px] uppercase tracking-[0.32em] font-extrabold text-navy/60">Certificate number</div>
        <div className="font-mono text-[15px] font-extrabold text-navy mt-0.5" data-testid="cert-number">
          {cert.cert_number}
        </div>
      </div>

      {/* Centred QR verify block */}
      <div className="mt-2 flex justify-center" data-testid="cert-qr-block">
        <QRBox url={verifyUrl} centered />
      </div>
    </div>
  </div>
);

const CertificateView = () => {
  const { API } = useContext(AuthContext);
  const { cert_number, course_id } = useParams();
  const navigate = useNavigate();
  const [cert, setCert] = useState(null);
  const [state, setState] = useState('loading'); // loading | ok | error | unauth
  const [errMsg, setErrMsg] = useState('');
  const isPublic = Boolean(cert_number);

  useEffect(() => {
    let cancelled = false;
    // Capture referral cert from the URL if present, so a visitor arriving via
    // a graduate's shared link is tagged before they navigate to sign up.
    try {
      const params = new URLSearchParams(window.location.search);
      const ref = params.get('ref');
      if (ref && /^(FDA|FDACADEMY)-/i.test(ref)) {
        window.localStorage.setItem('fda_ref_cert', ref.toUpperCase());
      }
    } catch (_) { /* ignore */ }
    (async () => {
      try {
        if (cert_number) {
          const res = await axios.get(`${API}/verify/${cert_number}`);
          if (cancelled) return;
          if (!res.data.holder_name) { setState('error'); setErrMsg('Certificate not found or invalid.'); return; }
          setCert(res.data);
          setState(res.data.valid ? 'ok' : 'error');
          if (!res.data.valid) setErrMsg(res.data.revoked ? `Revoked: ${res.data.revoked_reason || 'no reason provided'}` : 'Signature mismatch');
        } else if (course_id) {
          const token = window.localStorage.getItem('token');
          if (!token) { setState('unauth'); return; }
          const res = await axios.get(`${API}/lms/courses/${course_id}/certificate`, {
            headers: { Authorization: `Bearer ${token}` },
          });
          if (cancelled) return;
          setCert(res.data);
          setState('ok');
        } else {
          setState('error'); setErrMsg('Missing certificate identifier');
        }
      } catch (e) {
        if (!cancelled) {
          setState('error');
          setErrMsg(e.response?.data?.detail || 'Certificate not found');
        }
      }
    })();
    return () => { cancelled = true; };
  }, [API, cert_number, course_id]);

  const verifyUrl = useMemo(() => {
    if (!cert?.cert_number) return '';
    return `${window.location.origin}/certificate/${cert.cert_number}`;
  }, [cert]);

  const shareCardUrl = useMemo(() => {
    if (!cert?.cert_number) return '';
    return `${API}/verify/${cert.cert_number}/share-card.png`;
  }, [API, cert]);

  const downloadShareCard = async () => {
    if (!shareCardUrl) return;
    try {
      const res = await axios.get(shareCardUrl, { responseType: 'blob' });
      const url = window.URL.createObjectURL(res.data);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${cert.cert_number || 'fda-certificate'}-share.png`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success('Share image downloaded');
    } catch (e) {
      toast.error('Could not download share image');
    }
  };

  const share = async () => {
    if (!verifyUrl || !cert?.cert_number) return;
    const shareUrl = `${verifyUrl}?ref=${cert.cert_number}`;
    try {
      await navigator.clipboard.writeText(shareUrl);
      toast.success('Verification link copied');
    } catch (_) {
      toast.error('Could not copy link');
    }
  };

  const linkedInUrl = useMemo(() => {
    if (!cert?.course_title || !cert?.issued_at || !cert?.cert_number) return '';
    const issued = new Date(cert.issued_at);
    const params = new URLSearchParams({
      startTask: 'CERTIFICATION_NAME',
      name: cert.course_title,
      organizationName: cert.academy_director || 'Freeze Dry Academy',
      issueYear: String(issued.getFullYear()),
      issueMonth: String(issued.getMonth() + 1),
      certUrl: verifyUrl,
      certId: cert.cert_number,
    });
    return `https://www.linkedin.com/profile/add?${params.toString()}`;
  }, [cert, verifyUrl]);

  // One-click share to a LinkedIn feed post — the shared URL unfurls into
  // the branded PNG share-card (via the og:image meta above). Include the
  // ref query so signups from this share credit the graduate.
  const linkedInPostUrl = useMemo(() => {
    if (!verifyUrl || !cert?.cert_number) return '';
    const shareUrl = `${verifyUrl}?ref=${cert.cert_number}`;
    return `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(shareUrl)}`;
  }, [verifyUrl, cert]);

  // OpenGraph — so LinkedIn / Slack / Twitter previews show holder + course + share card
  useDocumentMeta({
    title: cert?.holder_name && cert?.course_title
      ? `${cert.holder_name} · ${cert.course_title} — Freeze Dry Academy Certificate`
      : 'Certificate of Completion — Freeze Dry Academy',
    description: cert?.holder_name && cert?.course_title
      ? `${cert.holder_name} completed "${cert.course_title}" on ${fmtDate(cert.issued_at)}. Certificate ${cert.cert_number} — verifiable at Freeze Dry Academy.`
      : 'Verify a Freeze Dry Academy course-completion certificate.',
    ogType: 'profile',
    ogImage: shareCardUrl || undefined,
    canonical: verifyUrl || undefined,
  });

  return (
    <div className="min-h-screen bg-navy-spotlight py-10 print:bg-white print:py-0" data-testid="certificate-view-page">
      {/* Toolbar — hidden in print */}
      <div className="container mx-auto px-6 mb-6 print:hidden">
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div className="flex items-center gap-2">
            <Button variant="ghost" onClick={() => navigate(-1)} className="text-background hover:bg-background/10" data-testid="cert-back-btn">
              <ArrowLeft className="w-4 h-4 mr-1.5" /> Back
            </Button>
            {state === 'ok' && cert?.cert_number && (
              <KudosButton
                targetType="graduate_cert"
                targetId={cert.cert_number}
                className="!bg-background/10 !border-background/25 !text-background hover:!bg-background/20"
              />
            )}
          </div>
          <div className="flex items-center gap-2">
            {state === 'ok' && (
              <>
                {linkedInUrl && (
                  <a
                    href={linkedInUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center rounded-full font-bold px-4 h-9 text-sm bg-[#0A66C2] text-white hover:bg-[#0a5aad] transition-colors"
                    data-testid="cert-linkedin-btn"
                  >
                    <Linkedin className="w-4 h-4 mr-1.5" /> Add to LinkedIn
                  </a>
                )}
                {linkedInPostUrl && (
                  <button
                    type="button"
                    onClick={async () => {
                      // Compose a caption that includes the certificate
                      // verify URL + PDF link so the LinkedIn post carries
                      // both the auto-unfurled share card AND a direct
                      // link to download the print-ready PDF.
                      const pdfUrl = `${(API || '').replace(/\/api$/, '')}/api/verify/${cert.cert_number}/certificate.pdf`;
                      const caption = `I just completed ${cert.course_title || 'a course'} at Freeze Dry Academy 🎓\n\n`
                        + `Certificate № ${cert.cert_number}\n`
                        + `Verify: ${verifyUrl}\n`
                        + `Download PDF: ${pdfUrl}\n\n`
                        + `#FreezeDryAcademy #Lyophilization`;
                      try {
                        await navigator.clipboard.writeText(caption);
                        toast.success('Caption + PDF link copied — paste into LinkedIn and post.', { duration: 6000 });
                      } catch { /* clipboard blocked, fall through */ }
                      window.open(linkedInPostUrl, '_blank', 'noopener,noreferrer');
                    }}
                    className="inline-flex items-center rounded-full font-bold px-4 h-9 text-sm border border-[#0A66C2] text-white bg-[#0A66C2]/20 hover:bg-[#0A66C2]/35 transition-colors"
                    data-testid="cert-linkedin-post-btn"
                    title="Copies a pre-composed caption (with your cert URL + PDF link) to your clipboard, then opens the LinkedIn share composer with the share card unfurled."
                  >
                    <Linkedin className="w-4 h-4 mr-1.5" /> Post to LinkedIn
                  </button>
                )}
                <Button
                  variant="outline"
                  onClick={downloadShareCard}
                  className="rounded-full font-bold bg-background/5 border-background/20 text-background hover:bg-background/10"
                  data-testid="cert-share-image-btn"
                >
                  <ImageIcon className="w-4 h-4 mr-1.5" /> Share image
                </Button>
                <Button
                  variant="outline"
                  onClick={share}
                  className="rounded-full font-bold bg-background/5 border-background/20 text-background hover:bg-background/10"
                  data-testid="cert-share-btn"
                >
                  <Share2 className="w-4 h-4 mr-1.5" /> Share verify link
                </Button>
                <Button
                  onClick={() => window.print()}
                  className="rounded-full font-bold bg-primary text-primary-foreground hover:bg-primary/90"
                  data-testid="cert-print-btn"
                >
                  <Printer className="w-4 h-4 mr-1.5" /> Print
                </Button>
                <a
                  href={`${API.replace(/\/api$/, '')}/api/verify/${cert.cert_number}/certificate.pdf`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center justify-center h-10 px-5 rounded-full font-bold border border-input hover:bg-accent text-sm"
                  data-testid="cert-pdf-btn"
                  title="Download the shareable PDF"
                >
                  <FileText className="w-4 h-4 mr-1.5" /> Download PDF
                </a>
              </>
            )}
          </div>
        </div>
      </div>

      {state === 'loading' && (
        <div className="text-center py-24 text-background" data-testid="cert-loading">
          <Loader2 className="w-10 h-10 animate-spin mx-auto mb-4" />
          <p>Loading certificate…</p>
        </div>
      )}
      {state === 'unauth' && (
        <div className="mx-auto max-w-md pro-card p-8 text-center bg-card" data-testid="cert-unauth">
          <XCircle className="w-8 h-8 text-destructive mx-auto mb-3" />
          <h1 className="font-heading text-xl font-extrabold text-foreground tracking-tight">Sign in required</h1>
          <p className="mt-2 text-sm text-foreground/70">Log in to see your own certificate.</p>
          <Button onClick={() => navigate('/login')} className="mt-6 rounded-full font-bold">Log in</Button>
        </div>
      )}
      {state === 'error' && (
        <div className="mx-auto max-w-md pro-card p-8 text-center bg-card" data-testid="cert-error">
          <XCircle className="w-8 h-8 text-destructive mx-auto mb-3" />
          <h1 className="font-heading text-xl font-extrabold text-foreground tracking-tight">Certificate not valid</h1>
          <p className="mt-2 text-sm text-foreground/70">{errMsg}</p>
          <Button variant="outline" onClick={() => navigate('/')} className="mt-6 rounded-full font-bold">Back to home</Button>
        </div>
      )}
      {state === 'ok' && cert && (
        <div className="w-full flex justify-center px-4 overflow-x-auto">
          <div className="origin-top scale-[0.6] sm:scale-[0.75] lg:scale-100 print:scale-100 transform">
            <CertificateSheet cert={cert} verifyUrl={verifyUrl} isPublic={isPublic} />
          </div>
        </div>
      )}

      {/* Public verify banner beneath the sheet */}
      {isPublic && cert && state === 'ok' && (
        <div className="container mx-auto px-6 mt-8 max-w-3xl print:hidden">
          <div className="pro-card p-5 bg-card flex items-center gap-3" data-testid="verify-valid-banner">
            <ShieldCheck className="w-6 h-6 text-emerald-600" />
            <div>
              <div className="font-heading font-extrabold text-foreground tracking-tight">Signature valid.</div>
              <div className="text-sm text-foreground/70">
                Issued by Freeze Dry Academy on {fmtDate(cert.issued_at)} to {cert.holder_name}. Certificate number{' '}
                <span className="font-mono font-bold">{cert.cert_number}</span>.
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CertificateView;
